<script>
   import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";

  const dispatch = createEventDispatcher();

  export let value = "";
  export let options = [];
  export let valueKey = "value";
  export let labelKey = "description";
  export let multiple = false;
  export let disabled = false;
  export let clearable = true;
  export let lockedValues = [];
  export let placeholder = "Selecciona una opción...";
  export let isStructuredDescription = false;
  export let paramStructuredName = [];

  export let remote = false;
  export let loading = false;
  export let hasMore = false;
  export let searchDebounceMs = 300;

  let rootElement;
  let searchInput;
  let isOpen = false;
  let search = "";

  let searchTimeout;
  let loadMoreRequested = false;
  let loadedOptionsCount = 0;

  onDestroy(() => {
    clearTimeout(searchTimeout);
  });

  onMount(() => {
    function handlePointerDown(event) {
      if (isOpen && rootElement && !rootElement.contains(event.target)) {
        isOpen = false;
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  });

  function handleSearchInput() {
    if (!remote) return;

    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      loadMoreRequested = false;
      dispatch("search", search.trim());
    }, searchDebounceMs);
  }

  function handleOptionsScroll(event) {
    if (!remote || loading || !hasMore || loadMoreRequested) return;

    const list = event.currentTarget;
    const distanceFromBottom =
      list.scrollHeight - list.scrollTop - list.clientHeight;

    if (distanceFromBottom > 24) return;

    loadMoreRequested = true;
    loadedOptionsCount = options.length;

    dispatch("loadMore");
  }

  $: if (
    loadMoreRequested &&
    (!hasMore || (!loading && options.length !== loadedOptionsCount))
  ) {
    loadMoreRequested = false;
  }

  $: if (disabled) {
    isOpen = false;
  }

  function getOptionValue(option, key = valueKey) {
    return option?.[key];
  }

  function getOptionLabel(option, key = labelKey) {
    if (!isStructuredDescription) return String(option?.[key] ?? "");
    return (paramStructuredName ?? [])
      .map((param) => option?.[param])
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map(String)
      .join(" - ");
  }

  function includesValue(values, optionValue) {
    return values.some((selectedValue) => Object.is(selectedValue, optionValue));
  }

  function isLockedValue(optionValue) {
    return multiple && includesValue(normalizedLockedValues, optionValue);
  }

  function toggleOption(option) {
    const optionValue = getOptionValue(option);
    if (isLockedValue(optionValue)) return;
    value = includesValue(selectedValues, optionValue)
      ? selectedValues.filter((selectedValue) => !Object.is(selectedValue, optionValue))
      : [...selectedValues, optionValue];
    dispatch("change", value);
  }

  function selectOption(option) {
    const nextValue = getOptionValue(option);

    if (Object.is(value, nextValue)) {
      isOpen = false;
      search = "";
      return;
    }

    value = nextValue;
    dispatch("change", value);
    isOpen = false;
    search = "";
  }

  function clearSelection() {
    value = multiple
      ? selectedValues.filter((selectedValue) => isLockedValue(selectedValue))
      : "";
    dispatch("change", value);
    search = "";
    isOpen = false;
  }

  async function toggleDropdown() {
    if (disabled) return;
    isOpen = !isOpen;
    if (isOpen) {
      await tick();
      searchInput?.focus();
    }
  }

  $: filteredOptions = remote
    ? options
    : options.filter((option) =>
        getOptionLabel(option, labelKey)
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      );
  $: selectedValues = multiple && Array.isArray(value) ? value : [];
  $: normalizedLockedValues = Array.isArray(lockedValues) ? lockedValues : [];
  $: selectedOption = multiple
    ? undefined
    : options.find((option) => Object.is(getOptionValue(option, valueKey), value));
  $: selectedOptions = multiple
    ? options.filter((option) => includesValue(selectedValues, getOptionValue(option, valueKey)))
    : [];
  $: hasSelection = multiple ? selectedOptions.length > 0 : selectedOption !== undefined;
  $: hasRemovableSelection = multiple
    ? selectedValues.some((selectedValue) => !isLockedValue(selectedValue))
    : hasSelection;
  $: triggerText = multiple
    ? selectedOptions.map((option) => getOptionLabel(option, labelKey)).join(", ") || placeholder
    : selectedOption
      ? getOptionLabel(selectedOption, labelKey)
      : placeholder;
</script>

<div class="select-box" bind:this={rootElement}>
  <div class="select-control">
    <button type="button" class="trigger" class:placeholder={!hasSelection} aria-expanded={isOpen} on:click={toggleDropdown} {disabled}>
      <span class="trigger-label">{triggerText}</span>
    </button>
    {#if hasSelection && hasRemovableSelection && !disabled && clearable}
      <button type="button" class="clear-selection" title="Clear selection" aria-label="Clear selection" on:click={clearSelection}>
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>
    {/if}
    <span class="select-arrow" aria-hidden="true"></span>
  </div>

  {#if isOpen}
    <div class="dropdown">
      <input
        class="search-input"
        type="search"
        placeholder="Buscar..."
        aria-label="Search options"
        bind:this={searchInput}
        bind:value={search}
        on:input={handleSearchInput}
      />
      <ul on:scroll={handleOptionsScroll}>
        {#each filteredOptions as option}
          <li>
            {#if multiple}
              <label
                class="option option-multiple"
                class:selected={includesValue(selectedValues, getOptionValue(option, valueKey))}
                class:locked={isLockedValue(getOptionValue(option, valueKey))}
                title={isLockedValue(getOptionValue(option, valueKey))
                  ? "Elemento existente: no se puede quitar"
                  : ""}
              >
                <input
                  type="checkbox"
                  checked={includesValue(selectedValues, getOptionValue(option, valueKey))}
                  disabled={isLockedValue(getOptionValue(option, valueKey))}
                  on:change={() => toggleOption(option)}
                />
                <span>{getOptionLabel(option, labelKey)}</span>
              </label>
            {:else}
              <button
                type="button"
                class="option"
                class:selected={Object.is(getOptionValue(option, valueKey), value)}
                on:click={() => selectOption(option)}
              >
                {getOptionLabel(option, labelKey)}
              </button>
            {/if}
          </li>
        {/each}
        {#if loading}
          <li class="loading">Loading...</li>
        {:else if filteredOptions.length === 0}
          <li class="no-results">No results</li>
        {/if}
      </ul>
    </div>
  {/if}
</div>

<style>
  .select-box {
    --dropdown-height: 38px;
    --dropdown-font-size: 0.875rem;
    --dropdown-border-color: var(--bs-border-color, #e2e6ea);
    --dropdown-border-radius: 0.4rem;
    --dropdown-focus-color: #86b7fe;
    --dropdown-focus-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
    position: relative;
    width: 100%;
    color: #333;
    font-family: inherit;
    font-size: var(--dropdown-font-size);
    line-height: 1.5;
  }

  .select-box,
  .select-box * {
    box-sizing: border-box;
  }

  .select-control {
    position: relative;
  }

  .trigger {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: var(--dropdown-height);
    padding: 0.4rem 3.4rem 0.4rem 0.55rem;
    border: 1px solid var(--dropdown-border-color);
    border-radius: var(--dropdown-border-radius);
    background: #fff;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
  }

  .trigger:hover:not(:disabled) {
    border-color: #b8c0c8;
  }

  .trigger:focus,
  .trigger[aria-expanded="true"] {
    border-color: var(--dropdown-focus-color);
    outline: 0;
    box-shadow: var(--dropdown-focus-shadow);
  }

  .trigger-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .clear-selection {
    position: absolute;
    top: 50%;
    right: 1.85rem;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #6c757d;
    font: inherit;
    line-height: 1;
    transform: translateY(-50%);
    cursor: pointer;
  }

  .clear-selection:hover {
    background: #f1f3f5;
    color: #343a40;
  }

  .clear-selection:focus-visible {
    outline: 2px solid var(--dropdown-focus-color);
    outline-offset: 1px;
  }

  .select-arrow {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    width: 0;
    height: 0;
    border-right: 0.3em solid transparent;
    border-left: 0.3em solid transparent;
    border-top: 0.35em solid currentColor;
    color: #6c757d;
    transform: translateY(-25%);
    pointer-events: none;
    transition: transform 0.15s ease;
  }

  .trigger[aria-expanded="true"] ~ .select-arrow {
    transform: translateY(-25%) rotate(180deg);
  }

  .trigger.placeholder {
    color: #6c757d;
  }

  .trigger:disabled {
    background: #f5f5f5;
    color: #8a8f94;
    cursor: not-allowed;
    opacity: 1;
  }

  .dropdown {
    position: absolute;
    top: calc(100% + 0.35rem);
    left: 0;
    z-index: 50;
    width: 100%;
    min-width: 12rem;
    overflow: hidden;
    border: 1px solid var(--dropdown-border-color);
    border-radius: calc(var(--dropdown-border-radius) + 0.1rem);
    background: #fff;
    box-shadow: 0 8px 24px rgba(33, 37, 41, 0.12);
  }

  .search-input {
    width: 100%;
    min-height: 36px;
    padding: 0.45rem 0.65rem;
    border: 0;
    border-bottom: 1px solid #e9ecef;
    border-radius: 0;
    outline: 0;
    background: #fff;
    color: inherit;
    font: inherit;
  }

  .search-input:focus {
    border-bottom-color: var(--dropdown-focus-color);
    box-shadow: inset 0 -1px 0 var(--dropdown-focus-color);
  }

  ul {
    max-height: 12rem;
    overflow-y: auto;
    margin: 0;
    padding: 0.3rem;
    list-style: none;
  }

  .option {
    display: block;
    width: 100%;
    padding: 0.45rem 0.6rem;
    border: 0;
    border-radius: 0.3rem;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .option:hover {
    background: #f4f6f8;
  }

  .option:focus-visible {
    outline: 2px solid var(--dropdown-focus-color);
    outline-offset: -2px;
  }

  .option.selected {
    background: #eaf3ff;
    color: #0d6efd;
    font-weight: 600;
  }

  .option.locked {
    cursor: not-allowed;
  }

  .option.locked:hover {
    background: #eaf3ff;
  }

  .option.locked input,
  .option.locked span {
    opacity: 0.7;
  }

  .option-multiple {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .option-multiple input {
    width: 1rem;
    height: 1rem;
    margin: 0;
    accent-color: #0d6efd;
  }

  .no-results,
  .loading {
    padding: 0.65rem;
    color: #6c757d;
    text-align: center;
    font-size: 0.85em;
  }
</style>
